import {
  FIND_USER_PORTAL_SEQUENCE,
  getStepBackendActionKey,
  HIDDEN_BACKEND_ACTION_KEYS,
  PREPARE_PASSWORD_RESET_SEQUENCE,
} from './workflowActionCatalog';

export const GROUP_TYPES = {
  PREPARE_PASSWORD_RESET: 'prepare_password_reset',
  FIND_USER_PORTAL: 'find_user_portal',
};

const GROUP_DEFINITIONS = {
  [GROUP_TYPES.PREPARE_PASSWORD_RESET]: {
    sequence: PREPARE_PASSWORD_RESET_SEQUENCE,
    title: 'Prepare Password Reset',
    subtitle: 'Find user, resolve portal, and generate secure reset link',
    badge: 'System',
    attentionBadge: 'Backend required',
    summary: 'Outputs: reset_link, reset_expires_at',
    showInNormalMode: true,
  },
  [GROUP_TYPES.FIND_USER_PORTAL]: {
    sequence: FIND_USER_PORTAL_SEQUENCE,
    title: 'Find User & Portal',
    subtitle: 'Lookup user by email and resolve the correct portal host',
    badge: 'Data Tool',
    attentionBadge: 'System helper',
    summary: 'Outputs: user.email, portal.reset_base_url',
    showInNormalMode: false,
    advancedOnly: true,
  },
};

function sequencesMatch(steps, startIndex, sequence) {
  if (startIndex + sequence.length > steps.length) return false;
  return sequence.every((key, offset) => {
    const stepKey = getStepBackendActionKey(steps[startIndex + offset]);
    return stepKey === key;
  });
}

function getExplicitGroupType(step) {
  return step?.config?.group_type || step?.config?.groupType || null;
}

function getExplicitGroupId(step) {
  return step?.config?.group_id || step?.config?.groupId || null;
}

export function detectGroupAt(steps, startIndex, options = {}) {
  const { advancedMode = false } = options;
  const step = steps[startIndex];
  if (!step) return null;

  const explicitType = getExplicitGroupType(step);
  const explicitId = getExplicitGroupId(step);
  if (explicitType && explicitId) {
    const siblings = [];
    let index = startIndex;
    while (index < steps.length && getExplicitGroupId(steps[index]) === explicitId) {
      siblings.push(steps[index]);
      index += 1;
    }
    const def = GROUP_DEFINITIONS[explicitType];
    if (def && (def.showInNormalMode || advancedMode)) {
      return buildGroupItem(explicitType, explicitId, siblings, startIndex, index - 1);
    }
  }

  for (const [groupType, def] of Object.entries(GROUP_DEFINITIONS)) {
    if (def.advancedOnly && !advancedMode) continue;
    if (!def.showInNormalMode && !advancedMode) continue;
    if (sequencesMatch(steps, startIndex, def.sequence)) {
      const slice = steps.slice(startIndex, startIndex + def.sequence.length);
      const groupId = getExplicitGroupId(slice[0]) || `grp_detected_${slice[0].id}`;
      return buildGroupItem(
        groupType,
        groupId,
        slice,
        startIndex,
        startIndex + def.sequence.length - 1,
      );
    }
  }

  return null;
}

function buildGroupItem(groupType, groupId, groupSteps, startIndex, endIndex) {
  const def = GROUP_DEFINITIONS[groupType];
  const groupConfig = groupSteps[0]?.config?.group_config
    || groupSteps[0]?.config?.groupConfig
    || extractGroupConfigFromSteps(groupSteps, groupType);

  return {
    type: 'group',
    groupType,
    groupId,
    steps: groupSteps,
    stepIds: groupSteps.map((s) => s.id),
    startIndex,
    endIndex,
    title: groupSteps[0]?.config?.group_name || def.title,
    subtitle: def.subtitle,
    badge: def.badge,
    attentionBadge: def.attentionBadge,
    summary: def.summary,
    groupConfig,
    definition: def,
  };
}

function extractGroupConfigFromSteps(steps, groupType) {
  const first = steps[0]?.config || {};
  if (groupType === GROUP_TYPES.PREPARE_PASSWORD_RESET) {
    return {
      action_name: 'Prepare Password Reset',
      email_source: first.email_source || '{{trigger.email}}',
      normalize_email: first.normalize_email !== false,
      silent_if_not_found: first.silent_if_not_found !== false,
      prevent_token_reuse: first.invalidate_previous !== false,
      require_active_portal_user: first.require_active_portal_user !== false,
      record_security_event: first.record_security_event !== false,
    };
  }
  if (groupType === GROUP_TYPES.FIND_USER_PORTAL) {
    return {
      action_name: 'Find User & Portal',
      email_source: first.email_source || '{{trigger.email}}',
      lookup_mode: first.lookup_mode || 'find_user_and_portal',
      normalize_email: first.normalize_email !== false,
      silent_if_not_found: first.silent_if_not_found !== false,
      require_active_portal_user: first.require_active_portal_user !== false,
      respect_isolation: first.respect_isolation !== false,
    };
  }
  return {};
}

export function buildDisplaySteps(steps, options = {}) {
  const { advancedMode = false } = options;
  const display = [];
  const warnings = [];
  let index = 0;

  while (index < steps.length) {
    const group = detectGroupAt(steps, index, { advancedMode });
    if (group) {
      display.push(group);
      index = group.endIndex + 1;
      continue;
    }

    const step = steps[index];
    const key = getStepBackendActionKey(step);
    if (!advancedMode && key && HIDDEN_BACKEND_ACTION_KEYS.has(key)) {
      warnings.push({
        id: step.id,
        message: 'This workflow contains advanced backend steps. Platform/admin review recommended.',
      });
      index += 1;
      continue;
    }

    display.push({ type: 'step', step, startIndex: index, endIndex: index });
    index += 1;
  }

  return { display, warnings };
}

export function createPreparePasswordResetSteps(config = {}, baseTimestamp = Date.now()) {
  const groupId = `grp_ppr_${baseTimestamp}`;
  const groupConfig = {
    action_name: config.action_name || 'Prepare Password Reset',
    email_source: config.email_source || '{{trigger.email}}',
    normalize_email: config.normalize_email !== false,
    silent_if_not_found: config.silent_if_not_found !== false,
    prevent_token_reuse: config.prevent_token_reuse !== false,
    require_active_portal_user: config.require_active_portal_user !== false,
    record_security_event: config.record_security_event !== false,
  };

  const shared = {
    group_type: GROUP_TYPES.PREPARE_PASSWORD_RESET,
    group_id: groupId,
    group_config: groupConfig,
    group_name: groupConfig.action_name,
  };

  return [
    {
      id: `step_${baseTimestamp}_0`,
      type: 'backend_action',
      label: 'Find User by Email',
      name: 'Find User by Email',
      config: {
        ...shared,
        group_index: 0,
        action_key: 'find_user_by_email',
        email_source: groupConfig.email_source,
        normalize_email: groupConfig.normalize_email,
        silent_if_not_found: groupConfig.silent_if_not_found,
        requires_backend: true,
      },
    },
    {
      id: `step_${baseTimestamp}_1`,
      type: 'backend_action',
      label: 'Resolve User Portal',
      name: 'Resolve User Portal',
      config: {
        ...shared,
        group_index: 1,
        action_key: 'resolve_user_portal',
        user_id: '{{find_user_by_email.user_id}}',
        email: '{{find_user_by_email.email}}',
        requires_backend: true,
      },
    },
    {
      id: `step_${baseTimestamp}_2`,
      type: 'backend_action',
      label: 'Generate Secure Reset Token',
      name: 'Generate Secure Reset Token',
      config: {
        ...shared,
        group_index: 2,
        action_key: 'generate_secure_reset_token',
        user_id: '{{find_user_by_email.user_id}}',
        email: '{{find_user_by_email.email}}',
        portal: '{{resolve_user_portal.portal}}',
        ttl_minutes: 30,
        invalidate_previous: groupConfig.prevent_token_reuse,
        requires_backend: true,
      },
    },
    {
      id: `step_${baseTimestamp}_3`,
      type: 'backend_action',
      label: 'Build Reset Link',
      name: 'Build Reset Link',
      config: {
        ...shared,
        group_index: 3,
        action_key: 'build_reset_link',
        reset_base_url: '{{resolve_user_portal.reset_base_url}}',
        email: '{{find_user_by_email.email}}',
        include_email: true,
        requires_backend: true,
      },
    },
  ];
}

export function createFindUserPortalSteps(config = {}, baseTimestamp = Date.now()) {
  const groupId = `grp_fup_${baseTimestamp}`;
  const groupConfig = {
    action_name: config.action_name || 'Find User & Portal',
    email_source: config.email_source || '{{trigger.email}}',
    lookup_mode: config.lookup_mode || 'find_user_and_portal',
    normalize_email: config.normalize_email !== false,
    silent_if_not_found: config.silent_if_not_found !== false,
    require_active_portal_user: config.require_active_portal_user !== false,
    respect_isolation: config.respect_isolation !== false,
  };

  const shared = {
    group_type: GROUP_TYPES.FIND_USER_PORTAL,
    group_id: groupId,
    group_config: groupConfig,
    group_name: groupConfig.action_name,
  };

  return [
    {
      id: `step_${baseTimestamp}_0`,
      type: 'backend_action',
      label: 'Find User by Email',
      name: 'Find User by Email',
      config: {
        ...shared,
        group_index: 0,
        action_key: 'find_user_by_email',
        email_source: groupConfig.email_source,
        normalize_email: groupConfig.normalize_email,
        silent_if_not_found: groupConfig.silent_if_not_found,
        requires_backend: true,
      },
    },
    {
      id: `step_${baseTimestamp}_1`,
      type: 'backend_action',
      label: 'Resolve User Portal',
      name: 'Resolve User Portal',
      config: {
        ...shared,
        group_index: 1,
        action_key: 'resolve_user_portal',
        user_id: '{{find_user_by_email.user_id}}',
        email: '{{find_user_by_email.email}}',
        requires_backend: true,
      },
    },
  ];
}

export function applyGroupConfigToSteps(steps, groupItem, nextConfig) {
  const groupId = groupItem.groupId;
  return steps.map((step) => {
    const stepGroupId = step.config?.group_id || step.config?.groupId;
    if (stepGroupId !== groupId) return step;

    const updatedConfig = { ...step.config, group_config: nextConfig, group_name: nextConfig.action_name };

    if (groupItem.groupType === GROUP_TYPES.PREPARE_PASSWORD_RESET) {
      const key = getStepBackendActionKey(step);
      if (key === 'find_user_by_email') {
        updatedConfig.email_source = nextConfig.email_source;
        updatedConfig.normalize_email = nextConfig.normalize_email;
        updatedConfig.silent_if_not_found = nextConfig.silent_if_not_found;
      }
      if (key === 'generate_secure_reset_token') {
        updatedConfig.invalidate_previous = nextConfig.prevent_token_reuse;
      }
    }

    if (groupItem.groupType === GROUP_TYPES.FIND_USER_PORTAL && getStepBackendActionKey(step) === 'find_user_by_email') {
      updatedConfig.email_source = nextConfig.email_source;
      updatedConfig.normalize_email = nextConfig.normalize_email;
      updatedConfig.silent_if_not_found = nextConfig.silent_if_not_found;
    }

    return { ...step, config: updatedConfig, label: step.config?.group_index === 0 ? nextConfig.action_name : step.label };
  });
}

export function removeGroupSteps(steps, groupItem) {
  const ids = new Set(groupItem.stepIds);
  return steps.filter((step) => !ids.has(step.id));
}

export function createStepsFromCatalogAction(action, baseTimestamp = Date.now()) {
  if (action.groupType === GROUP_TYPES.PREPARE_PASSWORD_RESET) {
    return createPreparePasswordResetSteps({}, baseTimestamp);
  }
  if (action.groupType === GROUP_TYPES.FIND_USER_PORTAL) {
    return createFindUserPortalSteps({}, baseTimestamp);
  }
  return null;
}
